import { useInfiniteQuery } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import type { Company } from '@/entities/company'
import { companyProjectsQueryOptions } from '@/entities/project'
import { CreateTaskDialog } from '@/features/create-task'
import { CompanyLinksBlock } from '@/features/edit-company'
import { flattenPages } from '@/shared/lib'
import { Breadcrumb } from '@/shared/ui/breadcrumb'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { CompanyPeople } from '@/widgets/company-people'
import { DocumentSlots } from '@/widgets/document-slots'
import { EntityPageHeader, EntityPageLayout, type EntityPageTab, Meta } from '@/widgets/entity-page'
import { InvoiceList } from '@/widgets/invoice-list'
import { MeetingList } from '@/widgets/meeting-list'
import { AllTaskList } from '@/widgets/task-list'
import { RequisitesGrid } from './company-requisites'
import { CustomerProjects } from './customer-projects'

// Табы хаба заказчика; team — только customer-admin.
export const CUSTOMER_HUB_TABS = [
  'projects',
  'tasks',
  'docs',
  'invoices',
  'meetings',
  'requisites',
  'team',
] as const
export type CustomerHubTab = (typeof CUSTOMER_HUB_TABS)[number]

const TAB_LABELS: Record<CustomerHubTab, string> = {
  projects: 'Проекты',
  tasks: 'Задачи',
  docs: 'Документы',
  invoices: 'Счета',
  meetings: 'Встречи',
  requisites: 'Реквизиты',
  team: 'Команда',
}

interface CustomerCompanyHubProps {
  company: Company
  isAdmin: boolean
  tab: CustomerHubTab | undefined
  onTabChange: (tab: CustomerHubTab) => void
}

// Хаб заказчика — customer-вариант страницы компании /companies/$company:
// шапка с названием и сводкой по проектам (customer_projects), табы Проекты /
// Задачи / Документы / Счета / Реквизиты (/ Команда — admin).
export function CustomerCompanyHub({
  company: c,
  isAdmin,
  tab,
  onTabChange,
}: CustomerCompanyHubProps) {
  const tabValues = CUSTOMER_HUB_TABS.filter((t) => t !== 'team' || isAdmin)
  const activeTab: CustomerHubTab = tab && tabValues.includes(tab) ? tab : 'projects'

  const tabs: EntityPageTab<CustomerHubTab>[] = tabValues.map((value) => ({
    value,
    label: TAB_LABELS[value],
    content: tabContent(c, value, isAdmin),
  }))

  const summary = c.customer_projects

  const header = (
    <EntityPageHeader
      breadcrumb={<Breadcrumb items={[{ label: 'Моя компания' }, { label: c.name }]} />}
      title={c.name}
      {...(summary
        ? {
            meta: (
              <Meta label="Проекты">
                <button
                  type="button"
                  title="К списку сделок"
                  onClick={() => onTabChange('projects')}
                  className="-mx-1 inline-flex rounded-control px-1 text-left text-sm underline-offset-4 transition-colors hover:bg-bg-elevated hover:underline hover:decoration-border-strong hover:decoration-dotted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
                >
                  <span className="text-text-primary">{summary.active} в работе</span>
                  <span aria-hidden className="px-1.5 text-text-tertiary">
                    ·
                  </span>
                  <span className="text-text-secondary">
                    {summary.won + summary.lost} завершено
                  </span>
                </button>
              </Meta>
            ),
          }
        : {})}
    />
  )

  return (
    <EntityPageLayout
      header={header}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      tabsLabel="Разделы компании"
    />
  )
}

function tabContent(c: Company, tab: CustomerHubTab, isAdmin: boolean): ReactNode {
  switch (tab) {
    case 'projects':
      return <CustomerProjects companyId={c.id} />
    case 'tasks':
      return <CustomerTasksTab company={c} />
    case 'docs':
      return (
        <DocumentSlots
          entityKind="company"
          entityId={c.id}
          withProjects
          docLink={(docId) => ({
            to: '/companies/$company/documents/$doc',
            params: { company: c.slug, doc: docId },
          })}
          projectDocLink={(docId, project) => ({
            to: '/$company/$project/documents/$doc',
            params: { company: c.slug, project: project.slug, doc: docId },
          })}
          projectLink={(project) => ({
            to: '/$company/$project',
            params: { company: c.slug, project: project.slug },
          })}
        />
      )
    case 'invoices':
      return (
        <InvoiceList
          source={{ kind: 'company', companyId: c.id }}
          isEmployee={false}
          invoiceLink={(invoiceId) => ({
            to: '/companies/$company/invoices/$invoice',
            params: { company: c.slug, invoice: invoiceId },
          })}
        />
      )
    case 'meetings':
      // Заказчику видны помеченные встречи его компании и её проектов; срез
      // считает бэкенд.
      return (
        <MeetingList
          scope={{ kind: 'company', id: c.id }}
          isEmployee={false}
          showProject
          meetingLink={(meetingId) => ({
            to: '/companies/$company/meetings/$meeting',
            params: { company: c.slug, meeting: meetingId },
          })}
        />
      )
    case 'requisites':
      return (
        <div className="flex flex-col gap-8">
          {isAdmin ? (
            // customer-admin правит реквизиты inline (PATCH с одним requisites).
            <section>
              <h2 className="label mb-3">Реквизиты</h2>
              <RequisitesGrid company={c} />
            </section>
          ) : (
            <CustomerRequisitesView company={c} />
          )}
          <section>
            <h2 className="label mb-3">Ссылки</h2>
            <CompanyLinksBlock company={c} editable={false} />
          </section>
        </div>
      )
    case 'team':
      return <CompanyPeople companyId={c.id} canManage />
  }
}

// Таб «Задачи» хаба: все задачи выданных заказчику сделок + создание задачи с
// выбором сделки из тех же (тот же источник, что таб «Проекты»).
function CustomerTasksTab({ company }: { company: Company }) {
  const [createOpen, setCreateOpen] = useState(false)
  const projectsQ = useInfiniteQuery(companyProjectsQueryOptions(company.id))
  // 403 (нет выданных сделок) — просто пустой список: кнопка задизейблена.
  const projects = flattenPages(projectsQ.data?.pages).map((d) => ({ id: d.id, title: d.title }))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="secondary"
          disabled={projects.length === 0}
          title={projects.length === 0 ? 'Нет доступных сделок' : undefined}
          onClick={() => setCreateOpen(true)}
        >
          <Icon name="plus" size={16} />
          Новая задача
        </Button>
      </div>
      <AllTaskList />
      {createOpen && (
        <CreateTaskDialog projects={projects} open onClose={() => setCreateOpen(false)} />
      )}
    </div>
  )
}

const REQ_LABELS: Array<[keyof Company['requisites'], string]> = [
  ['legal_name', 'Юридическое название'],
  ['inn', 'ИНН'],
  ['kpp', 'КПП'],
  ['ogrn', 'ОГРН'],
  ['legal_address', 'Юридический адрес'],
  ['bank_details', 'Банковские реквизиты'],
]

// Реквизиты read-only (customer-member; правка admin — отдельный вариант таба).
function CustomerRequisitesView({ company }: { company: Company }) {
  const rows = REQ_LABELS.filter(([key]) => company.requisites[key])
  if (rows.length === 0) {
    return <p className="text-sm text-text-tertiary">Реквизиты не заполнены.</p>
  }
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
      {rows.map(([key, label]) => (
        <div key={key} className="flex flex-col gap-0.5">
          <dt className="text-text-tertiary text-xs">{label}</dt>
          <dd className="text-sm text-text-primary">{company.requisites[key]}</dd>
        </div>
      ))}
    </dl>
  )
}
